// ─── Dashboard data shaping ───
// Pure helpers used by the Internal Command Center dashboard. No React.
//
// Every input is the raw `clients` array (and optional users + revenueSettings)
// pulled from existing contexts. Filters, role scoping, and aggregations are
// computed here and consumed by /components/dashboard/* presentational pieces.

import { DEFAULT_ACCOUNT_MANAGER } from './defaultAccountManager';

export const PIPELINE_FUNNEL_STAGES = ['lead', 'prospect', 'proposal', 'negotiation', 'active'];
export const ALL_FILTERABLE_STAGES = [
  { id: 'lead',         label: 'Lead',        bg: '#F1EFE8', fg: '#5F5E5A' },
  { id: 'prospect',     label: 'Prospect',    bg: '#E6F1FB', fg: '#0C447C' },
  { id: 'proposal',     label: 'Proposal',    bg: '#EEEDFE', fg: '#3C3489' },
  { id: 'negotiation',  label: 'Negotiation', bg: '#FAEEDA', fg: '#633806' },
  { id: 'active',       label: 'Active',      bg: '#E1F5EE', fg: '#085041' },
  { id: 'closed_won',   label: 'Closed Won',  bg: '#DCFCE7', fg: '#166534' },
  { id: 'lost',         label: 'Lost',        bg: '#FEE2E2', fg: '#991B1B' },
  { id: 'on_hold',      label: 'On Hold',     bg: '#F1F5F9', fg: '#475569' },
];
export const STAGE_BY_ID = ALL_FILTERABLE_STAGES.reduce((acc, s) => { acc[s.id] = s; return acc; }, {});

export const TIME_PERIODS = [
  { id: 'all',           label: 'All time' },
  { id: 'this_month',    label: 'This month' },
  { id: 'this_quarter',  label: 'This quarter' },
  { id: 'this_year',     label: 'This year' },
  { id: 'last_30',       label: 'Last 30 days' },
  { id: 'last_90',       label: 'Last 90 days' },
];

export function timePeriodToBounds(periodId) {
  if (!periodId || periodId === 'all') return null;
  const now = new Date();
  const start = new Date(now);
  const end = new Date(now);
  if (periodId === 'this_month') {
    start.setDate(1); start.setHours(0,0,0,0);
  } else if (periodId === 'this_quarter') {
    const q = Math.floor(now.getMonth() / 3);
    start.setMonth(q * 3, 1); start.setHours(0,0,0,0);
  } else if (periodId === 'this_year') {
    start.setMonth(0, 1); start.setHours(0,0,0,0);
  } else if (periodId === 'last_30') {
    start.setDate(start.getDate() - 30);
  } else if (periodId === 'last_90') {
    start.setDate(start.getDate() - 90);
  }
  return { start, end };
}

// ── Filtering ────────────────────────────────────────────────────────────
export function filterClients(clients, filters, currentUser, isSuperUser) {
  let out = Array.isArray(clients) ? clients.slice() : [];

  // Role scoping: AMs see only their book
  const userId = currentUser?.id || currentUser?._id;
  if (!isSuperUser && userId) {
    out = out.filter((c) => (c?.crm?.assignedTo === userId));
  }

  // AM filter (super user can pick a specific AM)
  if (filters?.amId && filters.amId !== 'all') {
    if (filters.amId === 'unassigned') {
      out = out.filter((c) => !c?.crm?.assignedTo);
    } else {
      out = out.filter((c) => c?.crm?.assignedTo === filters.amId);
    }
  }

  // State filter
  if (filters?.state && filters.state !== 'all') {
    out = out.filter((c) => (c?.company?.address?.state || '').toUpperCase() === filters.state);
  }

  // Stage filter
  if (filters?.stage && filters.stage !== 'all') {
    out = out.filter((c) => c?.crm?.stage === filters.stage);
  }

  // Time period filter — applied to client createdAt as the most useful signal
  const bounds = timePeriodToBounds(filters?.timePeriod);
  if (bounds) {
    out = out.filter((c) => {
      const ts = c?.createdAt || c?.updatedAt;
      if (!ts) return false;
      const d = new Date(ts);
      return d >= bounds.start && d <= bounds.end;
    });
  }

  return out;
}

// ── KPI Aggregations ─────────────────────────────────────────────────────
function sumSummary(clients, key) {
  return clients.reduce((s, c) => s + (Number(c?.summary?.[key]) || 0), 0);
}

function enrolledLives(clients) {
  let total = 0;
  for (const c of clients) {
    const list = c?.employees || [];
    for (const e of list) {
      const status = e?.enrollmentStatus;
      if (status === 'enrolled' || status === 'opted_in') total += 1;
    }
  }
  return total;
}

// Pull the broker commission % from the admin spread allocation (or default 15%).
export function brokerCommissionPct(revenueSettings) {
  const list = revenueSettings?.spreadAllocation || [];
  const m = list.find((x) => /broker/i.test(x?.category || ''));
  return m ? (Number(m.percentage) || 0) / 100 : 0.15;
}
export function vendorPct(revenueSettings) {
  const list = revenueSettings?.spreadAllocation || [];
  const m = list.find((x) => /vendor/i.test(x?.category || ''));
  return m ? (Number(m.percentage) || 0) / 100 : 0.25;
}
export function underwritingPct(revenueSettings) {
  const list = revenueSettings?.spreadAllocation || [];
  const m = list.find((x) => /underwrit|hospital/i.test(x?.category || ''));
  return m ? (Number(m.percentage) || 0) / 100 : 0.30;
}
export function hcMarginPct(revenueSettings) {
  const list = revenueSettings?.spreadAllocation || [];
  const m = list.find((x) => /healthcues|margin|administration/i.test(x?.category || ''));
  return m ? (Number(m.percentage) || 0) / 100 : 0.30;
}

export function computeKpis(clients, revenueSettings) {
  const totalLives = sumSummary(clients, 'totalEmployees');
  const eligible = sumSummary(clients, 'eligible');
  const annualFica = sumSummary(clients, 'annualEmployerFica');
  const annualHcRevenue = sumSummary(clients, 'annualHcRevenue');
  const enrolled = enrolledLives(clients);

  const eligibleRate = totalLives > 0 ? eligible / totalLives : 0;
  const enrolledRate = eligible > 0 ? enrolled / eligible : 0;

  // Annual spread = annual HC revenue (revenue model). Commission and margin
  // are derived from spread allocation percentages.
  const annualSpread = annualHcRevenue;
  const brokerPct = brokerCommissionPct(revenueSettings);
  const marginPct = hcMarginPct(revenueSettings);
  const annualCommission = annualSpread * brokerPct;
  const hcMargin = annualHcRevenue * marginPct;

  return {
    totalLives,
    eligible,
    eligibleRate,
    annualFica,
    annualCommission,
    totalClients: clients.length,
    enrolled,
    enrolledRate,
    annualHcRevenue,
    hcMargin,
    annualSpread,
    brokerPct,
    marginPct,
  };
}

// ── Pipeline funnel counts ───────────────────────────────────────────────
export function pipelineCounts(clients) {
  const by = {};
  ALL_FILTERABLE_STAGES.forEach((s) => { by[s.id] = 0; });
  clients.forEach((c) => {
    const s = c?.crm?.stage;
    if (s && by[s] !== undefined) by[s] += 1;
  });
  return by;
}

// ── Top clients (sorted by FICA descending; clients without summary at end) ─
export function topClientsByFica(clients, limit = 10) {
  return clients.slice()
    .sort((a, b) => (Number(b?.summary?.annualEmployerFica) || 0) - (Number(a?.summary?.annualEmployerFica) || 0))
    .slice(0, limit);
}

// ── States with employee counts ──────────────────────────────────────────
export function statesWithEmployeeCounts(clients) {
  const by = {};
  clients.forEach((c) => {
    const st = (c?.company?.address?.state || '').toUpperCase();
    if (!st) return;
    by[st] = (by[st] || 0) + (Number(c?.summary?.totalEmployees) || 0);
  });
  return Object.entries(by)
    .map(([state, employees]) => ({ state, employees }))
    .sort((a, b) => b.employees - a.employees);
}

// ── AMs with client counts (super user view) ─────────────────────────────
export function amsWithClientCounts(clients, users) {
  const counts = {};
  let unassigned = 0;
  clients.forEach((c) => {
    const aid = c?.crm?.assignedTo;
    if (!aid) { unassigned += 1; return; }
    counts[aid] = (counts[aid] || 0) + 1;
  });
  const result = users
    .filter((u) => u.role === 'account_manager' || u.role === 'super_user')
    .map((u) => ({
      id: u.id || u._id,
      name: `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email,
      email: u.email,
      count: counts[u.id || u._id] || 0,
    }))
    .sort((a, b) => b.count - a.count);
  if (unassigned > 0) {
    result.push({ id: 'unassigned', name: 'Unassigned', email: '', count: unassigned, isUnassigned: true });
  }
  return result;
}

// ── Per-AM commission breakdown ──────────────────────────────────────────
export function amCommissionBreakdown(clients, users, revenueSettings) {
  const brokerPct = brokerCommissionPct(revenueSettings);
  // Build buckets keyed by AM id
  const buckets = {};
  clients.forEach((c) => {
    const aid = c?.crm?.assignedTo || '__unassigned__';
    if (!buckets[aid]) buckets[aid] = { id: aid, count: 0, eligible: 0, spread: 0, isUnassigned: aid === '__unassigned__' };
    buckets[aid].count += 1;
    buckets[aid].eligible += Number(c?.summary?.eligible) || 0;
    // Sum spread across ALL stages so the per-AM rollup matches the
    // aggregate "Total annual spread" KPI on the same panel. Filtering only
    // by active/renewal here meant proposal-stage clients (where the deal is
    // about to close) silently showed $0 commission, which contradicted the
    // headline number the user already saw above.
    buckets[aid].spread += Number(c?.summary?.annualHcRevenue) || 0;
  });
  const userById = (users || []).reduce((acc, u) => { acc[u.id || u._id] = u; return acc; }, {});
  return Object.values(buckets)
    .map((b) => {
      const u = userById[b.id];
      let initials, name;
      if (b.isUnassigned) {
        initials = 'U'; name = 'Unassigned';
      } else if (u) {
        name = `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email || 'Unknown';
        const f = (u.firstName || '?')[0] || '?'; const l = (u.lastName || '')[0] || '';
        initials = (f + l).toUpperCase();
      } else if (b.id === DEFAULT_ACCOUNT_MANAGER.id) {
        name = DEFAULT_ACCOUNT_MANAGER.fullName; initials = 'RY';
      } else {
        name = 'Unknown AM'; initials = '?';
      }
      return {
        ...b,
        name,
        initials,
        commission: b.spread * brokerPct,
      };
    })
    .sort((a, b) => b.spread - a.spread);
}

// ── Follow-ups (overdue / today / this week) ─────────────────────────────
export function bucketFollowUps(clients) {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const tomorrow = new Date(today); tomorrow.setDate(tomorrow.getDate() + 1);
  const weekFromNow = new Date(today); weekFromNow.setDate(weekFromNow.getDate() + 7);

  const overdue = []; const todayList = []; const thisWeek = [];
  clients.forEach((c) => {
    const fu = c?.crm?.followUp;
    if (!fu?.action || !fu?.dueDate) return;
    const due = new Date(fu.dueDate);
    const item = {
      clientId: c.id,
      clientName: c?.company?.legalName || c?.company?.dba || 'Unnamed',
      action: fu.action,
      dueDate: fu.dueDate,
      assignedTo: fu.assignedTo || c?.crm?.assignedTo || null,
      daysDiff: Math.floor((due - today) / 86400000),
    };
    if (due < today) overdue.push(item);
    else if (due >= today && due < tomorrow) todayList.push(item);
    else if (due >= tomorrow && due < weekFromNow) thisWeek.push(item);
  });
  // Sort within each bucket: overdue by most overdue first; today/week by soonest
  overdue.sort((a, b) => a.daysDiff - b.daysDiff);
  todayList.sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate));
  thisWeek.sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate));
  return { overdue, today: todayList, thisWeek };
}

// ── Trend snapshots (placeholder — empty until real history exists) ──────
export function buildTrendSnapshots(clients) {
  // We don't yet persist monthly snapshots; signal insufficient history to UI.
  return { months: [], hasHistory: false };
}

export function formatRelative(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const diffMs = Date.now() - d.getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hour${hrs !== 1 ? 's' : ''} ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days} day${days !== 1 ? 's' : ''} ago`;
  return d.toLocaleDateString();
}
